import { formatUsd, GalleryShare, groupDeckSections, LocationDetail, LocationList, type Location } from '@mtg/shared';
import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { api, errorText } from '../api.ts';
import { plural } from '../format.ts';
import { useApi } from '../useApi.ts';

export function CollectionPage() {
  const { data, error } = useApi(() => api('/api/locations', LocationList), []);
  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const all = data.locations;
  const total = { cards: all.reduce((n, l) => n + l.cardCount, 0), value: all.reduce((n, l) => n + l.value, 0) };
  const drawers = all.filter((l) => l.kind !== 'deck');
  const decks = all.filter((l) => l.kind === 'deck');

  return (
    <div className="stack">
      <h1>Collection</h1>
      <p className="lead">
        {plural(total.cards, 'card')} · {formatUsd(total.value)}
      </p>
      <QuickSearch />
      <h2>Drawers</h2>
      <LocationTable locations={drawers} />
      <h2>Decks</h2>
      {decks.length ? <DeckSections decks={decks} /> : <p className="muted">No decks yet.</p>}
      {decks.length > 0 && <ShareAllDecks />}
      <NewDeck />
    </div>
  );
}

/** Jumps straight to Search with a name, so finding a card is one step from the home page. */
function QuickSearch() {
  const [, navigate] = useLocation();
  const [name, setName] = useState('');
  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        navigate(`/search?${new URLSearchParams({ name: name.trim() })}`);
      }}
    >
      <input
        type="search"
        className="search-box"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Find a card…"
        aria-label="Find a card"
        enterKeyHint="search"
        autoCapitalize="off"
      />
    </form>
  );
}

/** Decks in the same sections and order as the shared /decks page. */
function DeckSections({ decks }: { decks: Location[] }) {
  const sections = groupDeckSections(decks, (d) => ({ name: d.name, section: d.gallery?.section ?? '', order: d.gallery?.order ?? null }));
  return (
    <>
      {sections.map((s) => (
        <section key={s.title ?? ''} className="stack home-deck-section">
          {s.title && (
            <h3 className="gallery-heading small-heading">
              <span>{s.title}</span>
            </h3>
          )}
          <LocationTable locations={s.items} />
        </section>
      ))}
    </>
  );
}

function LocationTable({ locations }: { locations: Location[] }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Where</th>
          <th className="num">Cards</th>
          <th className="num">Value</th>
        </tr>
      </thead>
      <tbody>
        {locations.map((l) => (
          <tr key={l.id}>
            <td>
              <Link href={`/locations/${l.id}`}>{l.name}</Link>
              {l.label && <span className="muted"> · {l.label}</span>}
              {l.gallery?.label && <span className="gallery-label">{l.gallery.label}</span>}
            </td>
            <td className="num">{l.cardCount.toLocaleString()}</td>
            <td className="num">{formatUsd(l.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Every deck, read-only, at /decks for friends. Turning it off closes every deck page reached through it. */
function ShareAllDecks() {
  const share = useApi(() => api('/api/gallery', GalleryShare), []);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function set(shared: boolean) {
    setError(null);
    try {
      share.setData(await api('/api/gallery', GalleryShare, { method: 'PATCH', body: { shared } }));
      setCopied(false);
    } catch (e) {
      setError(errorText(e));
    }
  }

  if (!share.data) return share.error ? <p className="error">{share.error}</p> : null;
  const url = share.data.shared ? `${location.origin}/decks` : null;
  return (
    <section className="panel stack">
      <p>
        <strong>Share all decks</strong>
      </p>
      {url ? (
        <div className="row">
          <input readOnly value={url} aria-label="Link to all decks" onFocus={(e) => e.target.select()} />
          <button type="button" className="btn" onClick={() => void navigator.clipboard.writeText(url).then(() => setCopied(true))}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <a className="btn" href={url} target="_blank" rel="noreferrer">
            Open
          </a>
          <button type="button" className="btn" onClick={() => void set(false)}>
            Stop sharing
          </button>
        </div>
      ) : (
        <div className="row">
          <button type="button" className="btn primary" onClick={() => void set(true)}>
            Share all decks
          </button>
          <span className="muted small">Shows every deck, read-only, at {location.host}/decks, new ones included. Drawers stay private.</span>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

function NewDeck() {
  const [, navigate] = useLocation();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const created = await api('/api/decks', LocationDetail, { body: { name } });
      navigate(`/locations/${created.location.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <form className="row" onSubmit={submit}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New deck name" maxLength={100} aria-label="New deck name" />
      <button type="submit" className="btn" disabled={!name.trim()}>
        Create deck
      </button>
      {error && <p className="error">{error}</p>}
    </form>
  );
}
